import type { Unit } from '../enums'
import { formatNumber } from '../utils/format-number'

import type { FieldKey } from './field-key'
import { displayUnitSymbol } from './units'

type FieldFormat = {
  /** A key into `UNITS`, never a symbol — the spelling lives there. */
  unit?: Unit
  decimalPlaces?: number
  /** Pass '' to disable digit grouping (years, IDs). */
  digitGroupSeparator?: string
}

/**
 * How a field's value is rendered: its unit, how many decimals it carries, and
 * whether digits are grouped.
 *
 * Plain data about the field, not text and not presentation. It sits in
 * `shared/` because the PDF needs it as much as the app does; a frontend-only
 * home forces the server to restate every unit, and the two spellings drift.
 *
 * A field with no entry has no unit and no fixed precision.
 */
export const FIELD_FORMAT: Partial<Record<FieldKey, FieldFormat>> = {
  // ─── Areas ──────────────────────────────────────────────────────────
  livingArea: { unit: 'SQUARE_METER', decimalPlaces: 1 },
  usableArea: { unit: 'SQUARE_METER', decimalPlaces: 1 },
  landArea: { unit: 'SQUARE_METER', decimalPlaces: 1 },
  gardenArea: { unit: 'SQUARE_METER', decimalPlaces: 1 },
  balconyArea: { unit: 'SQUARE_METER', decimalPlaces: 1 },
  wallArea: { unit: 'SQUARE_METER', decimalPlaces: 1 },

  // ─── Counts ─────────────────────────────────────────────────────────
  numberOfRooms: { decimalPlaces: 1 },
  numberOfBathrooms: { decimalPlaces: 0 },
  numberOfFloors: { decimalPlaces: 0 },
  numberOfIndoorParkingSpaces: { decimalPlaces: 0 },
  numberOfOutdoorParkingSpaces: { decimalPlaces: 0 },
  floorNumber: { decimalPlaces: 0 },

  // ─── Years ──────────────────────────────────────────────────────────
  constructionYear: { decimalPlaces: 0, digitGroupSeparator: '' },
  renovationYear: { decimalPlaces: 0, digitGroupSeparator: '' },

  // ─── Energy ─────────────────────────────────────────────────────────
  energyConsumption: { unit: 'KWH_PER_SQM_YEAR', decimalPlaces: 1 },

  // ─── Prices ─────────────────────────────────────────────────────────
  marketPrice: { unit: 'EURO' },
  potentialPrice: { unit: 'EURO' },

  // ─── Rooms ──────────────────────────────────────────────────────────
  length: { unit: 'METER', decimalPlaces: 2 },
  width: { unit: 'METER', decimalPlaces: 2 },
  height: { unit: 'METER', decimalPlaces: 2 },
  perimeter: { unit: 'METER', decimalPlaces: 2 },
  area: { unit: 'SQUARE_METER', decimalPlaces: 2 },

  // ─── Cost entries ───────────────────────────────────────────────────
  flatRateCost: { unit: 'EURO', decimalPlaces: 2 },
  quantity: { decimalPlaces: 0 },
  // Three, where a cost entry's Anzahl is whole: a price row may take two and
  // a half hours of a fitter, or an area to the square centimetre.
  rowCount: { decimalPlaces: 3 },

  // ─── Finances ───────────────────────────────────────────────────────
  netAmount: { unit: 'EURO', decimalPlaces: 2 },
  grossAmount: { unit: 'EURO', decimalPlaces: 2 },
  amount: { unit: 'EURO', decimalPlaces: 2 },
  vatRate: { unit: 'PERCENT', decimalPlaces: 2 },
  percent: { unit: 'PERCENT', decimalPlaces: 2 },
  paymentTermDays: { unit: 'DAY', decimalPlaces: 0 },
}

/** The unit a field's value carries, or '' where it has none. */
export const fieldUnit = (key: FieldKey): string => {
  const unit = FIELD_FORMAT[key]?.unit
  return displayUnitSymbol(unit)
}

/** A field's value with the unit and precision the field declares. Every
 *  surface that prints a number reads it from here, so a screen and a document
 *  cannot render the same figure differently. */
export function formatFieldValue(
  key: FieldKey,
  value: number | null | undefined,
): string | null {
  if (value === null || value === undefined) return null
  const { unit, decimalPlaces, digitGroupSeparator } = FIELD_FORMAT[key] ?? {}
  const number = formatNumber(value, {
    maximumFractionDigits: decimalPlaces,
    useGrouping: digitGroupSeparator === '' ? false : undefined,
  })
  const symbol = displayUnitSymbol(unit)
  return symbol ? `${number} ${symbol}` : number
}
